import * as React from 'react'
import { getAffiliateIdFromStorage as defaultGetAffiliateIdFromStorage } from '@repo/affiliate-id'
import { useIsLoggedInQuery, userQueryFetcher } from 'auth/hooks'
import { PaymentStatus } from '@repo/graphql-types'
import type { CoinPack } from '@repo/graphql-types'
import { usePaymentsBuyCoinsMutation } from '../../gen/stripe'
import { useUserCoinBalanceQuery } from '../../gen/coins'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import * as Sentry from '@sentry/react'
import { SentryFilterCoinsTopUp } from '@repo/sentry-utils/constants'
import { defaultCurrency } from 'cf-i18n/constants'
import { mapCurrencyToAvailableCurrencyCode } from '@repo/cf-user/hooks/use-user-data'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import type { UserQuery } from 'codegen/generated/user'

export interface CoinsTopUpProps {
  coinPack: CoinPack
}

interface FailingReason {
  type: 'noUser' | 'notSubscribed' | 'topUpFailed'
  message: string
}

export interface TopUpReturn {
  type: 'coinsTopUp'
  paymentsStatus?: PaymentStatus
  message?: string | null
  error: FailingReason | null
}

export interface UseCoinsTopUpOptions {
  // Injected so tests can supply a fake without module mocks; production uses
  // the real localStorage-backed reader from `@repo/affiliate-id`.
  getAffiliateIdFromStorage?: () => string | null
}

export function useCoinsTopUp({
  getAffiliateIdFromStorage = defaultGetAffiliateIdFromStorage,
}: UseCoinsTopUpOptions = {}) {
  const [isPending, setIsPending] = React.useState(false)
  const { data: isUserLoggedIn } = useIsLoggedInQuery()
  const buyCoinsMutation = usePaymentsBuyCoinsMutation()
  const {
    data: userCoinBalanceData,
    refetch: refetchUserCoinBalance,
    isPending: isUserCoinBalanceLoading,
  } = useUserCoinBalanceQuery(undefined, { enabled: isUserLoggedIn })

  const userBalance = React.useRef(userCoinBalanceData?.userBalance.balance)

  React.useEffect(() => {
    if (userCoinBalanceData?.userBalance.balance !== undefined && !isUserCoinBalanceLoading) {
      userBalance.current = userCoinBalanceData.userBalance.balance
    }
  }, [userCoinBalanceData, isUserLoggedIn, isUserCoinBalanceLoading])

  const { openModal } = useUserAuthModal()

  // oxlint-disable-next-line complexity
  const handleCoinsTopUp = async ({
    coinPack,
  }: CoinsTopUpProps): Promise<TopUpReturn | undefined> => {
    if (buyCoinsMutation.isPending) {
      return
    }

    //If not logged in, open auth modal
    if (!isUserLoggedIn) {
      openModal()
      return
    }

    setIsPending(true)

    let userData: UserQuery | null = null
    try {
      //Fetch latest user data
      userData = await userQueryFetcher()
    } catch (error) {
      Sentry.captureException(
        new Error(
          `useUserQuery fetcher in coins top-up failed with error: ${
            (error as Error)?.message ? (error as Error)?.message : (error as string)
          }`,
        ),
        {
          tags: {
            component: SentryFilterCoinsTopUp,
          },
        },
      )
    }

    if (!userData) {
      setIsPending(false)
      return {
        type: 'coinsTopUp',
        error: {
          type: 'noUser',
          message: '',
        },
        paymentsStatus: undefined,
        message: null,
      }
    }

    const userHasAllAccess = userData.me?.user?.subscriptionAccess.allAccess.isSubscribed
    const userHasStudioAi = userData.me?.user?.subscriptionAccess.studioAi.isSubscribed

    if (!userHasAllAccess && !userHasStudioAi) {
      setIsPending(false)

      //User is not eligible for coins top-up
      return {
        type: 'coinsTopUp',
        error: {
          type: 'notSubscribed',
          message: '',
        },
        paymentsStatus: undefined,
        message: null,
      }
    }

    const currencyCode = mapCurrencyToAvailableCurrencyCode(
      userData.me?.user?.currency.code || defaultCurrency.code,
    )

    let response = null
    try {
      // Forward the captured ref for apps that can't share the affwp_ref cookie
      // cross-domain. On cookie-bearing domains BE reads the cookie directly.
      const affiliateId = getAffiliateIdFromStorage()
      response = await buyCoinsMutation.mutateAsync({
        coinPack,
        currencyCode,
        ...(affiliateId ? { affiliateId } : {}),
      })
    } catch (error) {
      Sentry.captureException(
        new Error(
          `usePaymentsBuyCoinsMutation mutateAsync in coins top-up failed: No data returned ${
            (error as Error)?.message ? (error as Error)?.message : (error as string)
          }`,
        ),
        {
          tags: {
            component: SentryFilterCoinsTopUp,
          },
        },
      )
    }

    if (!response) {
      setIsPending(false)

      return {
        type: 'coinsTopUp',
        error: { type: 'topUpFailed', message: '' },
        paymentsStatus: undefined,
        message: null,
      }
    }

    if (response.paymentsBuyCoins.paymentStatus === PaymentStatus.Succeeded) {
      sendAmplitudeEvent(AmplitudeEvents.PURCHASED_COIN_PACK, {
        coin_pack: coinPack,
      })

      let attempts = 0
      const maxAttempts = 10
      const delayBetweenAttemptsMs = 2000

      //Refetch the user coin balance until it is updated or we reach max attempts
      try {
        while (attempts < maxAttempts) {
          const resp = await refetchUserCoinBalance()

          if (
            userBalance.current !== undefined &&
            userBalance.current !== resp.data?.userBalance.balance
          ) {
            break
          }

          attempts++
          // oxlint-disable-next-line promise/avoid-new
          await new Promise(resolve => setTimeout(resolve, delayBetweenAttemptsMs))
        }
      } catch (error) {
        Sentry.captureException(
          new Error(
            `refetching user coin balance in coins top-up failed: ${
              (error as Error)?.message ? (error as Error)?.message : (error as string)
            }`,
          ),
          {
            tags: {
              component: SentryFilterCoinsTopUp,
            },
          },
        )
      }
    }

    setIsPending(false)
    return {
      type: 'coinsTopUp',
      error: null,
      paymentsStatus: response.paymentsBuyCoins.paymentStatus,
      message: response.paymentsBuyCoins.payInvoiceUrl,
    }
  }

  return { buyCoins: handleCoinsTopUp, isPending: isPending || buyCoinsMutation.isPending }
}
